import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, Check } from "lucide-react";
import api from "@/lib/api";

const SEV_COLOR = { high: "#EF4444", medium: "#F5821F", low: "#71717A" };

export default function NotificationBell({ testid = "notif-bell" }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try { const r = await api.get("/notifications/unread-count"); setCount(r.data.count || 0); } catch { /* noop */ }
  }, []);
  const loadItems = useCallback(async () => {
    try { const r = await api.get("/notifications?limit=20"); setItems(r.data); } catch { /* noop */ }
  }, []);

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 15000); return () => clearInterval(t); }, [loadCount]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => { const n = !open; setOpen(n); if (n) await loadItems(); };
  const markAll = async () => { await api.post("/notifications/read-all"); await loadItems(); await loadCount(); };
  const markOne = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    if (n.episode_id) { window.location.href = `/alertes?tab=episodes&episode=${n.episode_id}`; return; }
    await loadItems(); await loadCount();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid={testid} className="relative text-muted-foreground hover:text-foreground p-2 transition-colors">
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span data-testid={`${testid}-badge`} className="absolute top-0 right-0 min-w-[16px] h-4 px-1 rounded-full bg-[#F5821F] text-white text-[10px] font-bold flex items-center justify-center">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid={`${testid}-panel`} className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto glass rounded-lg border border-border z-[1400] p-2 shadow-xl">
          <div className="flex items-center justify-between px-2 py-1.5">
            <span className="text-sm font-bold">Notifications</span>
            <button onClick={markAll} data-testid="notif-read-all" className="text-[11px] text-[#2ec5db] hover:underline flex items-center gap-1">
              <Check className="w-3 h-3" /> Tout marquer lu
            </button>
          </div>
          {items.length === 0 && <p className="text-xs text-muted-foreground px-2 py-6 text-center">Aucune notification</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => markOne(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-2 py-2 rounded-md hover:bg-black/5 border-l-2 transition-colors ${n.read ? "border-transparent opacity-60" : ""}`}
              style={{ borderLeftColor: n.read ? "transparent" : (SEV_COLOR[n.severity] || "#F5821F") }}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold truncate">{n.title}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{new Date(n.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{n.message}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
